// Built-in TypeScript/JavaScript ProjectAnalyzer (proposal §3.1, §3.5, Phase E).
//
// Resolves cross-file `calls` / `type_reference` edges using lightweight
// import-based analysis: each file's import declarations are parsed with
// ts.createSourceFile (single-file, no Program), module specifiers are resolved
// via ts.resolveModuleName, and the imported names are matched against known
// exported symbols from the per-file analysis cache.
//
// This avoids ts.createProgram entirely — resolution is O(changed files), not
// O(project). Targets outside the project (node_modules, .d.ts) are left
// unresolved; their bare-name edge is preserved.

import { readFileSync } from "node:fs";
import { posix, relative as pathRelative } from "node:path";

import ts from "typescript";

import type { GraphEdge } from "../../types.js";
import type { AnalyzerCapability, FileAnalysisResult } from "../types.js";
import type {
  ProjectAnalysisInput,
  ProjectAnalysisResult,
  ProjectAnalyzer,
} from "../project-analyzer.js";

const FILE_EXTENSIONS = [
  ".ts",
  ".tsx",
  ".js",
  ".jsx",
  ".mts",
  ".cts",
  ".mjs",
  ".cjs",
] as const;

const CAPABILITIES: AnalyzerCapability[] = ["calls", "type_references"];

/** Edge kinds whose bare-name `to` this analyzer resolves to a symbolId. */
const RESOLVABLE_KINDS = new Set<GraphEdge["kind"]>(["calls", "type_reference"]);

/** Default compiler options when the project has no usable tsconfig.json. */
const DEFAULT_OPTIONS: ts.CompilerOptions = {
  allowJs: true,
  checkJs: false,
  noEmit: true,
  target: ts.ScriptTarget.Latest,
  module: ts.ModuleKind.ESNext,
  moduleResolution: ts.ModuleResolutionKind.Bundler,
  esModuleInterop: true,
  skipLibCheck: true,
  allowImportingTsExtensions: true,
};

function hasExtension(filePath: string): boolean {
  return FILE_EXTENSIONS.some((ext) => filePath.endsWith(ext));
}

function loadCompilerOptions(projectRoot: string): ts.CompilerOptions {
  try {
    const configPath = ts.findConfigFile(
      projectRoot,
      ts.sys.fileExists,
      "tsconfig.json",
    );
    if (!configPath) return DEFAULT_OPTIONS;

    const read = ts.readConfigFile(configPath, ts.sys.readFile);
    if (read.error || !read.config) return DEFAULT_OPTIONS;

    const parsed = ts.parseJsonConfigFileContent(
      read.config,
      ts.sys,
      projectRoot,
    );
    return { ...parsed.options, noEmit: true };
  } catch {
    return DEFAULT_OPTIONS;
  }
}

/** Convert an absolute declaration file path to a project-relative posix path. */
function projectRelative(
  absFile: string,
  projectRoot: string,
): string | null {
  const rel = posix.normalize(
    pathRelative(projectRoot, absFile).split(/[\\/]/).join("/"),
  );
  if (rel.startsWith("..")) return null;
  if (rel.split("/").includes("node_modules")) return null;
  return rel;
}

// ---------------------------------------------------------------------------
// Lightweight import-based resolution
// ---------------------------------------------------------------------------

/** Maps an imported name to the resolved project-relative file it comes from. */
type ImportMap = Map<string, { resolvedFile: string; originalName: string }>;

/**
 * Parse a source file's import declarations and build a map from each imported
 * binding name to its origin module (resolved to a project-relative path).
 */
function buildImportMap(
  sourceFile: ts.SourceFile,
  options: ts.CompilerOptions,
  projectRoot: string,
): ImportMap {
  const map: ImportMap = new Map();

  for (const statement of sourceFile.statements) {
    if (
      !ts.isImportDeclaration(statement) ||
      !ts.isStringLiteral(statement.moduleSpecifier)
    ) {
      continue;
    }
    const specifier = statement.moduleSpecifier.text;
    const resolved = ts.resolveModuleName(
      specifier,
      sourceFile.fileName,
      options,
      ts.sys,
    );
    const resolvedFileName = resolved.resolvedModule?.resolvedFileName;
    if (!resolvedFileName) continue;
    const relFile = projectRelative(resolvedFileName, projectRoot);
    if (!relFile) continue;

    const clause = statement.importClause;
    if (!clause) continue;

    if (clause.name) {
      map.set(clause.name.text, {
        resolvedFile: relFile,
        originalName: "default",
      });
    }

    if (clause.namedBindings && ts.isNamedImports(clause.namedBindings)) {
      for (const element of clause.namedBindings.elements) {
        const localName = element.name.text;
        const originalName = element.propertyName?.text ?? localName;
        map.set(localName, { resolvedFile: relFile, originalName });
      }
    }

    if (
      clause.namedBindings &&
      ts.isNamespaceImport(clause.namedBindings)
    ) {
      map.set(clause.namedBindings.name.text, {
        resolvedFile: relFile,
        originalName: "*",
      });
    }
  }

  return map;
}

/**
 * Look up a symbol name in a single file's analysis result.
 *
 * Checks (in order):
 * 1. Exported symbol with matching name
 * 2. Class member with matching bare name (for `s.listen()` → `Server.listen`)
 * 3. Re-exported symbol: follows re_exports edges to the source file
 */
function findSymbolInFile(
  name: string,
  relPath: string,
  files: Map<string, FileAnalysisResult>,
  options: ts.CompilerOptions,
  projectRoot: string,
): string | null {
  const result = files.get(relPath);
  if (!result) return null;

  // Direct export match
  for (const sym of result.symbols) {
    if (sym.exported && sym.name === name) return sym.symbolId;
  }

  // Class member match (bare method name)
  for (const sym of result.symbols) {
    if (!sym.parentSymbolId) continue;
    const dotIdx = sym.name.lastIndexOf(".");
    const member = dotIdx >= 0 ? sym.name.slice(dotIdx + 1) : sym.name;
    if (member === name) return sym.symbolId;
  }

  // Follow re-export chain: if this file re-exports from another, check there.
  for (const edge of result.edges) {
    if (edge.kind !== "re_exports") continue;
    let targetFile = edge.to;
    if (!files.has(targetFile)) {
      const absFrom = posix.join(
        projectRoot.split(/[\\/]/).join("/"),
        relPath,
      );
      const resolved = ts.resolveModuleName(
        targetFile,
        absFrom,
        options,
        ts.sys,
      );
      const resolvedPath = resolved.resolvedModule?.resolvedFileName;
      if (!resolvedPath) continue;
      const rel = projectRelative(resolvedPath, projectRoot);
      if (!rel) continue;
      targetFile = rel;
    }
    const found = findSymbolInFile(name, targetFile, files, options, projectRoot);
    if (found) return found;
  }

  return null;
}

/**
 * Resolve a bare name to a symbolId by looking only at the files that the
 * current file directly imports. No global index is built.
 *
 * Strategies:
 * 1. Named import: `validateToken` → import map → target file's symbols
 * 2. Namespace access: `ns.bar` → namespace import → target file's symbols
 * 3. Member fallback: bare `listen` → scan imported files' class members
 */
function resolveFromImports(
  name: string,
  importMap: ImportMap,
  files: Map<string, FileAnalysisResult>,
  options: ts.CompilerOptions,
  projectRoot: string,
): string | null {
  // 1. Named import
  const direct = importMap.get(name);
  if (direct) {
    if (direct.originalName === "*") return null;
    return (
      findSymbolInFile(direct.originalName, direct.resolvedFile, files, options, projectRoot) ??
      findSymbolInFile(name, direct.resolvedFile, files, options, projectRoot)
    );
  }

  // 2. Namespace access: `auth.validateToken`
  const dotIdx = name.indexOf(".");
  if (dotIdx > 0) {
    const nsName = name.slice(0, dotIdx);
    const memberName = name.slice(dotIdx + 1);
    const ns = importMap.get(nsName);
    if (ns && ns.originalName === "*") {
      return findSymbolInFile(memberName, ns.resolvedFile, files, options, projectRoot);
    }
  }

  // 3. Member fallback: check imported files for a class member with this name
  const seen = new Set<string>();
  for (const entry of importMap.values()) {
    if (seen.has(entry.resolvedFile)) continue;
    seen.add(entry.resolvedFile);
    const result = files.get(entry.resolvedFile);
    if (!result) continue;
    for (const sym of result.symbols) {
      if (!sym.parentSymbolId) continue;
      const mdot = sym.name.lastIndexOf(".");
      const member = mdot >= 0 ? sym.name.slice(mdot + 1) : sym.name;
      if (member === name) return sym.symbolId;
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// ProjectAnalyzer
// ---------------------------------------------------------------------------

export class TypeScriptProjectAnalyzer implements ProjectAnalyzer {
  readonly language = "typescript";
  readonly capabilities = CAPABILITIES;
  readonly fileExtensions = FILE_EXTENSIONS;

  async analyzeProject(
    input: ProjectAnalysisInput,
  ): Promise<ProjectAnalysisResult> {
    const { projectRoot, files, changedFiles } = input;

    const handledFiles = [...files.keys()].filter(hasExtension);
    if (handledFiles.length === 0) {
      return { resolvedEdges: [], handledFiles: [] };
    }

    const options = loadCompilerOptions(projectRoot);
    const resolvedEdges: GraphEdge[] = [];

    // Only re-resolve files that actually changed. Unchanged files already
    // carry post-resolution edges from the previous run (cached).
    const needsResolve = changedFiles ?? new Set(handledFiles);

    for (const relPath of handledFiles) {
      if (!hasExtension(relPath)) continue;
      const result = files.get(relPath);
      if (!result) continue;

      const resolvable = result.edges.filter((e) => RESOLVABLE_KINDS.has(e.kind));
      if (resolvable.length === 0) continue;

      if (!needsResolve.has(relPath)) {
        for (const edge of resolvable) {
          resolvedEdges.push(edge);
        }
        continue;
      }

      // Parse only this file to extract its import map.
      const absPath = posix.join(
        projectRoot.split(/[\\/]/).join("/"),
        relPath,
      );
      let sourceText: string;
      try {
        sourceText = readFileSync(absPath, "utf-8");
      } catch {
        for (const edge of resolvable) {
          resolvedEdges.push(edge);
        }
        continue;
      }
      const sourceFile = ts.createSourceFile(
        absPath,
        sourceText,
        ts.ScriptTarget.Latest,
        true,
      );
      const importMap = buildImportMap(sourceFile, options, projectRoot);

      // Resolve each edge by looking only at directly imported files.
      for (const edge of resolvable) {
        if (edge.to.includes("#")) {
          resolvedEdges.push(edge);
          continue;
        }
        const resolved = resolveFromImports(
          edge.to,
          importMap,
          files,
          options,
          projectRoot,
        );
        if (resolved) {
          resolvedEdges.push({
            ...edge,
            to: resolved,
            evidence: edge.evidence.map((e) => ({
              ...e,
              detail: `${e.detail} → ${resolved}`,
            })),
          });
        } else {
          resolvedEdges.push(edge);
        }
      }
    }

    return { resolvedEdges, handledFiles };
  }
}
